/**
 * Put the Samsung platform inventory somewhere the fleet can read it, without
 * anyone standing in front of the television.
 *
 * WHY THIS EXISTS. The facts that unblock the remaining Tizen tickets - the
 * real `tizenId` of every installed app (TIZEN-14), the actual method names on
 * `webapis.systemcontrol` (TIZEN-13), which keys `registerKey` accepted
 * (TIZEN-15) - all lived on a diagnostic page reachable only by typing a URL.
 * The Samsung widget has no address bar, so reading them meant a person, a
 * panel, and a photograph. Two attempts to add a remote-key shortcut both
 * failed on the same rock: the keys we reached for were not being delivered by
 * this remote.
 *
 * The console ring buffer was already the answer and nobody had used it. It is
 * uploaded by the existing `log_capture` command (see logBuffer.ts and
 * diagnostics.ts), which the CMS can fire at any device from a desk. So the app
 * simply SAYS all of this at boot, and the inventory arrives in the fleet
 * console on request - no chord, no camera, no trip to the bench.
 *
 * It is written as ordinary console output rather than new telemetry fields on
 * purpose: this is discovery, and the shape of what we want to know changes
 * every time the panel answers. A schema would have to be migrated each time;
 * a log line does not. Once a fact here turns out to matter permanently, it
 * graduates into the heartbeat as a real field.
 *
 * Nothing here may throw, and nothing here may print a secret: the buffer is
 * uploaded to the fleet console and kept.
 */

import { tizenKeyClaimReport } from "./tizenKeys";
import { offlineShellStatus } from "./offlineShell";

const TAG = "[tizen-inventory]";

/** Run a read that may not exist, may throw, or may do both. */
function safe(fn: () => unknown): string {
  try {
    const v = fn();
    if (v === undefined || v === null || v === "") return "—";
    return String(v);
  } catch (err) {
    return `!! ${err instanceof Error ? err.message : "threw"}`;
  }
}

/** The own+prototype keys of a platform namespace (Samsung's are prototype-backed). */
function methodsOf(obj: unknown): string[] {
  try {
    if (!obj || typeof obj !== "object") return [];
    const out = new Set<string>();
    for (const k of Object.keys(obj as object)) out.add(k);
    const proto = Object.getPrototypeOf(obj as object);
    if (proto && proto !== Object.prototype) {
      for (const k of Object.getOwnPropertyNames(proto)) {
        if (k !== "constructor") out.add(k);
      }
    }
    return Array.from(out).sort();
  } catch {
    return [];
  }
}

interface TizenWindow {
  tizen?: {
    application?: {
      getAppsInfo?: (
        ok: (list: Array<{ id?: string; name?: string }>) => void,
        err: (e: unknown) => void
      ) => void;
    };
  };
  webapis?: Record<string, unknown>;
  b2bapis?: Record<string, unknown>;
}

/**
 * Log the inventory once. Safe on every platform - off Tizen there is no
 * `webapis` and it logs a single line saying so, which is itself the answer
 * when a panel we believed was running the signed widget turns out not to be.
 */
export function logTizenInventory(): void {
  try {
    if (typeof window === "undefined") return;
    const w = window as unknown as TizenWindow;
    if (!w.tizen && !w.webapis) return; // not a Samsung panel; say nothing

    console.info(`${TAG} userAgent=${safe(() => navigator.userAgent)}`);
    // Whether this panel has an offline shell at all. Registration happens
    // asynchronously, so at boot this usually reads "not attempted" - the value
    // that matters is the one a LATER capture shows, and on a widget we expect
    // a cross-origin scope failure (TIZEN-12).
    console.info(`${TAG} offlineShell=${safe(() => offlineShellStatus())}`);
    console.info(`${TAG} registerKey=${safe(() => tizenKeyClaimReport())}`);

    // The namespaces, and what is actually callable on each. This is what says
    // whether `reboot` or `setSystemReboot` is the real name on this firmware,
    // and which power methods exist (deliberately unwired - see canControlPower).
    const webapis = w.webapis;
    console.info(`${TAG} webapis.*=${methodsOf(webapis).join(" ") || "—"}`);
    for (const ns of ["systemcontrol", "productinfo", "network"]) {
      const obj = (webapis as Record<string, unknown> | undefined)?.[ns];
      console.info(
        `${TAG} webapis.${ns}.*=${methodsOf(obj).join(" ") || "absent"}`
      );
    }

    // Hospitality-only surface (samsung-hospitality-tv-port memory note): every
    // b2b capability in foyerBridge.ts is gated on this namespace, and it was
    // never enumerated here - only in `?probe=1`, which a transcluded widget
    // cannot open (no address bar). On a signage panel this is expected to
    // read "absent"; on a hospitality panel, "absent" here is the finding that
    // answers whether our cert/declaration grants it at all.
    console.info(
      `${TAG} b2bapis.b2bcontrol.*=${
        methodsOf(w.b2bapis?.b2bcontrol).join(" ") || "absent"
      }`
    );
    // `was.*` (launchApp et al) and `tvinfo.*` were extracted from a shipping
    // SI's widget, undocumented and partner-gated - their global location on
    // OUR firmware/cert is unconfirmed, so guessing a path here would risk a
    // false "absent" being read as a real answer. Left as an open bench item
    // rather than a speculative check; see samsung-hospitality-tv-port memory.

    // The installed-app harvest. Callback-style (pre-Promise Tizen API), and
    // `application.info` is declared in config.xml. One line per app so a long
    // list stays readable in the uploaded capture.
    const app = w.tizen?.application;
    if (typeof app?.getAppsInfo !== "function") {
      console.info(`${TAG} apps=getAppsInfo absent`);
      return;
    }
    app.getAppsInfo(
      (list) => {
        try {
          const rows = Array.isArray(list) ? list : [];
          console.info(`${TAG} apps=${rows.length} installed`);
          for (const a of rows) {
            console.info(`${TAG} app ${a?.name ?? "?"} = ${a?.id ?? "?"}`);
          }
        } catch (e) {
          console.info(`${TAG} apps=render threw ${String(e)}`);
        }
      },
      (e) =>
        console.info(
          `${TAG} apps=denied/threw ${e instanceof Error ? e.message : String(e)}`
        )
    );
  } catch {
    // Discovery must never be the reason a television fails to start.
  }
}
